"use client";

import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "nexus-theme";

function readTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return (document.documentElement.dataset.theme as Theme) || "light";
}

// Theme lives on documentElement.dataset.theme (set pre-render in layout to avoid
// flash). This hook mirrors it into React state and keeps every consumer in sync
// via a window "themechange" event so multiple toggles never drift apart.
export function useTheme() {
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    setTheme(readTheme());
    const handler = () => setTheme(readTheme());
    window.addEventListener("themechange", handler);
    return () => window.removeEventListener("themechange", handler);
  }, []);

  const applyTheme = useCallback((next: Theme) => {
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage can throw in private mode; the attribute change is what matters
    }
    window.dispatchEvent(new Event("themechange"));
  }, []);

  const toggleTheme = useCallback(() => {
    applyTheme(readTheme() === "dark" ? "light" : "dark");
  }, [applyTheme]);

  return { theme, setTheme: applyTheme, toggleTheme };
}
